import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Cake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { computeAge, formatAge } from '@/lib/age';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const txt = (lang, fr, ru, md, uk) => ({ fr, ru, md, uk }[lang] || fr);

// Memes animations que BonusCelebration (feux d'artifice + confettis)
const fireworkStyles = `
@keyframes bday-firework-particle {
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; }
}
@keyframes bday-confetti-fall {
  0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(110px) rotate(720deg); opacity: 0; }
}
@keyframes bday-pop-in {
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}
`;

function Particle({ delay, color, tx, ty }) {
  return (
    <div
      className="absolute w-2 h-2 rounded-full"
      style={{
        backgroundColor: color,
        '--tx': `${tx}px`,
        '--ty': `${ty}px`,
        animation: `bday-firework-particle 1.2s ease-out ${delay}s forwards`,
        left: '50%',
        top: '40%',
      }}
    />
  );
}

function Confetti({ delay, color, left }) {
  return (
    <div
      className="absolute w-2 h-3 rounded-sm"
      style={{
        backgroundColor: color,
        left: `${left}%`,
        top: '-5px',
        animation: `bday-confetti-fall 2.2s ease-in ${delay}s forwards`,
      }}
    />
  );
}

/**
 * Anniversaire de l'utilisateur connecte (Pavel 2026-08-02) : le jour J, un
 * pop-up plein ecran avec feux d'artifice lui souhaite son anniversaire dans
 * sa langue. Une seule fois par an et par appareil (garde localStorage).
 * Les collegues, eux, recoivent la notification existante + la carte du
 * tableau de bord (BirthdayBanner).
 */
export default function BirthdayCelebration() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [me, setMe] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user?.id || user._isPreview) return;
    const key = `lpc-bday-self-${user.id}-${new Date().getFullYear()}`;
    if (localStorage.getItem(key)) return;

    const check = async () => {
      try {
        const res = await axios.get(`${API}/birthdays/today`);
        const mine = (res.data || []).find((u) => u.id === user.id);
        if (mine) {
          setMe(mine);
          setShow(true);
          localStorage.setItem(key, '1');
        }
      } catch (e) { /* silencieux */ }
    };
    const timer = setTimeout(check, 1200);
    return () => clearTimeout(timer);
  }, [user?.id]);

  if (!show || !me) return null;

  const firstName = (me.name || '').split(' ')[0];
  const age = computeAge(me.birthday);
  const colors = ['#FF6B9D', '#FFD700', '#4ECDC4', '#FF8C42', '#A78BFA', '#FF6B6B', '#45B7D1', '#96CEB4'];
  const particles = [];
  for (let i = 0; i < 26; i++) {
    const angle = (i / 26) * Math.PI * 2;
    const dist = 60 + Math.random() * 45;
    particles.push({
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist,
      color: colors[i % colors.length],
      delay: Math.random() * 0.35,
    });
  }
  const confettis = [];
  for (let i = 0; i < 26; i++) {
    confettis.push({
      left: Math.random() * 100,
      color: colors[i % colors.length],
      delay: Math.random() * 1.2,
    });
  }

  return (
    <>
      <style>{fireworkStyles}</style>
      <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShow(false)}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {confettis.map((c, i) => (
            <Confetti key={i} {...c} />
          ))}
        </div>

        <div
          className="relative bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-sm mx-4 text-center overflow-hidden"
          style={{ animation: 'bday-pop-in 0.5s ease-out forwards' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="birthday-celebration"
        >
          <div className="absolute inset-0 pointer-events-none">
            {particles.map((p, i) => (
              <Particle key={i} {...p} />
            ))}
          </div>

          <div className="relative mx-auto w-20 h-20 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center shadow-lg mb-4">
            <Cake className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {txt(language,
              `Joyeux anniversaire, ${firstName} ! 🎂`,
              `С днём рождения, ${firstName}! 🎂`,
              `La mulți ani, ${firstName}! 🎂`,
              `З днем народження, ${firstName}! 🎂`)}
          </h2>
          {age !== null && (
            <p className="text-3xl font-bold text-rose-500 mb-2">
              {formatAge(age, language)} 🎉
            </p>
          )}
          <p className="text-base text-gray-600 mb-6">
            {txt(language,
              'Toute l\'équipe te souhaite une superbe journée !',
              'Вся команда желает тебе отличного дня!',
              'Toată echipa îți urează o zi minunată!',
              'Вся команда бажає тобі чудового дня!')}
          </p>

          <Button onClick={() => setShow(false)} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold text-base py-3">
            {txt(language, 'Merci ! 🎉', 'Спасибо! 🎉', 'Mulțumesc! 🎉', 'Дякую! 🎉')}
          </Button>
        </div>
      </div>
    </>
  );
}
